import React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@uhg-abyss/web/tools/styled';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { ProviderSelectDrawerLoadingSpinner } from '../ProviderSelectDrawerLoadingSpinner';

const StyledTableContainer = styled('div', {
  height: '350px',
  overflowY: 'scroll',
});

const StyledTable = styled('table', {
  width: '100%',
  marginTop: '16px',
});

const StyledResultsNotFoundTableRow = styled('tr', {
  borderTop: '1px solid $gray4',
  borderBottom: 'none',
  '&:hover': {
    backgroundColor: 'transparent',
  },
  cursor: 'default',
});

const StyledResultsNotFoundTableData = styled('td', {
  padding: '8px 15px',
  color: 'black',
  fontSize: '16px',
  dynamic: ({ isError }) => {
    return {
      color: isError ? '$error1' : 'black',
      fontWeight: isError ? 'bold' : 'normal',
    };
  },
});

export const ProviderTableData = ({
  colWidths,
  data,
  renderRow,
  noResultsMessage = 'No results found',
  isLoading,
  ariaLoadingLabel,
  isError,
  errorMessage,
}) => {
  if (isLoading) {
    return (
      <ProviderSelectDrawerLoadingSpinner
        isLoading={isLoading}
        ariaLoadingLabel={ariaLoadingLabel}
      />
    );
  }

  if (isError) {
    return (
      <StyledTableContainer>
        <StyledTable>
          <StyledResultsNotFoundTableRow>
            <StyledResultsNotFoundTableData isError={isError} colSpan="2">
              <Layout.Group>
                <IconSymbol icon="error" color="$error1" variant="outlined" />
                {errorMessage}
              </Layout.Group>
            </StyledResultsNotFoundTableData>
          </StyledResultsNotFoundTableRow>
        </StyledTable>
      </StyledTableContainer>
    );
  }

  if (data.length === 0) {
    return (
      <StyledTableContainer>
        <StyledTable>
          <StyledResultsNotFoundTableRow>
            <StyledResultsNotFoundTableData colSpan="2">
              {noResultsMessage}
            </StyledResultsNotFoundTableData>
          </StyledResultsNotFoundTableRow>
        </StyledTable>
      </StyledTableContainer>
    );
  }

  return (
    <React.Fragment>
      <StyledTableContainer>
        <StyledTable>
          <colgroup>
            <For each="width" of={colWidths}>
              <col key={`colwidth-${width}`} style={{ width }} />
            </For>
          </colgroup>
          <tbody>{renderRow(data)}</tbody>
        </StyledTable>
      </StyledTableContainer>
    </React.Fragment>
  );
};

ProviderTableData.propTypes = {
  colWidths: PropTypes.arrayOf(PropTypes.string).isRequired,
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  renderRow: PropTypes.func.isRequired,
  noResultsMessage: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  ariaLoadingLabel: PropTypes.string.isRequired,
  isError: PropTypes.bool,
  errorMessage: PropTypes.string,
};

ProviderTableData.defaultProps = {
  noResultsMessage: 'No results found',
  isError: false,
  errorMessage: '',
};
